import AecData from '../common/AecModelData.js';
import {chooseMainModel} from "../levels/LevelUtils.js";

const LEVEL_EXT   = 'Autodesk.AEC.LevelsExtension';

const namespace = AutodeskNamespace('Autodesk.AEC');
const extensionName = 'Autodesk.AEC.Hypermodeling';

/**
 * The hypermodeling extension allows placing Revit 2D sheets at their correct location in the 3D model
 * This relies on the 2D-3D transform available from AEC model data, and as such is limited to a certain
 * type of sheets, namely floor plans (see {@link supports2DTo3DTransform})
 *
 * Future work: allow placing any sheet (such as PDFs) manually
 *              Open the 3D model from the sheet model (inverse workflow from the current one)
 */
export default class HypermodelingExtension extends Autodesk.Viewing.Extension {
    constructor(viewer, options = {}) {
        super(viewer, options);

        this.loadedSheets = {};
        this.sheetsPerLevelCache = {};
        this.zPositionPerLevel = {};
        this.onModelRootLoaded = this.onModelRootLoaded.bind(this);
        this.onModelChanged = this.onModelChanged.bind(this);
    }

    async load() {
        // Bear in mind that in case the application wants to load the LevelsExtension with specific options argument,
        // It should load the LevelsExtension *before* loading hypermodeling extension.
        this.levelExtension = await this.viewer.loadExtension(LEVEL_EXT);

        this.addListeners();

        // Currently this extension works only for extending a 3D model with 2D sheets.
        // Could be extended to support the opposite workflow.
        let models = this.viewer.impl.modelQueue().getModels();
        const model3D = chooseMainModel(models);
        this.init(model3D);

        return true;
    }

    onModelChanged({model}) {
        if (model.is2d()) {
            return;
        }

        let models = this.viewer.impl.modelQueue().getModels();
        const model3D = chooseMainModel(models);
        if (model3D !== this.model3D) {
            this.unloadAll();
            this.init(model3D);
        }
    }

    init(model3D) {
        this.loadedSheets = {};
        this.sheetsPerLevelCache = {};
        this.zPositionPerLevel = {};
        this.floorSelector = this.levelExtension.floorSelector;

        this.model3D = model3D;
        if (!model3D) {
            return;
        }

        this.rootNode = this.model3D.getDocumentNode().getRootNode();
        this.document = this.rootNode.getDocument();
        this.aec = this.rootNode.getAecModelData();
    }

    unload() {
        this.removeListeners();

        this.unloadAll();

        this.floorSelector = null;
        this.model3D = null;
        this.rootNode = null;
        this.document = null;
        this.aec = null;

        return true;
    }

    addListeners() {
        this.viewer.addEventListener(Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, this.onModelRootLoaded);
        this.viewer.addEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, this.onModelChanged);
        this.viewer.addEventListener(Autodesk.Viewing.MODEL_REMOVED_EVENT, this.onModelChanged);
    }

    removeListeners() {
        this.viewer.removeEventListener(Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, this.onModelRootLoaded);
        this.viewer.removeEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, this.onModelChanged);
        this.viewer.removeEventListener(Autodesk.Viewing.MODEL_REMOVED_EVENT, this.onModelChanged);
    }

    getHashNameForViewportAndLevel(viewport, levelIndex) {
        return viewport.viewportGuid + '.' + levelIndex;
    }

    onModelRootLoaded({ model }) {
        if (model.is3d()) {
            return;
        }

        // Since this is an async event, and multiple sheets might be loading at the same time, have to make sure
        // we get the relevant level data for the correct one.
        const hash = model.loader.options.customHash;
        const loadedSheet = this.loadedSheets[hash];
        if (!loadedSheet || !loadedSheet.loading) {
            Autodesk.Viewing.Private.logger.warn('Model root loaded called for sheet not being loaded');
            return;
        }

        // Only after the model root has being loaded can we fetch the unit scale.
        // And this is just in time to set the placement transform before it's used
        // to create the meshes.
        const sheetUnitScale = model.getUnitScale();
        const vpTransform = AecData.get2DTo3DMatrix(loadedSheet.viewport, sheetUnitScale);

        const level = this.floorSelector.floorData[loadedSheet.levelIndex];

        const placementTf = this.model3D.getModelToViewerTransform();
        if (placementTf) {
            vpTransform.multiplyMatrices(placementTf, vpTransform);
        }
        // Move an inch so it matches the floor. Reverses the hack in LevelUtils.js.
        // Add another tenth of an inch to avoid z-fighting with the floor (no particular reason for this number,
        // might be possible to lower it)
        // Add another small value per level to avoid z-fighting in overlapping sheets
        if (!Object.prototype.hasOwnProperty.call(this.zPositionPerLevel, loadedSheet.levelIndex)) {
            this.zPositionPerLevel[loadedSheet.levelIndex] = 0;
        } else {
            this.zPositionPerLevel[loadedSheet.levelIndex] += 0.001;
        }
        const zOffsetHack = 1/12 + 1/120 + this.zPositionPerLevel[loadedSheet.levelIndex];

        const pos = new THREE.Vector3();
        pos.setFromMatrixPosition(vpTransform);
        //The level's zMin already contains the transform, so it's ok to overwrite.
        pos.z = level.zMin + zOffsetHack;
        vpTransform.setPosition(pos);

        model.setPlacementTransform(vpTransform);
    }

    /**
     * Returns the sheets available for the desired level index.
     * @param {number} levelIndex
     * @returns {array} - the sheets corresponding to the input level index
     */
    getAvailableSheetsForLevel(levelIndex) {
        if (this.sheetsPerLevelCache[levelIndex]) {
            return this.sheetsPerLevelCache[levelIndex];
        }

        const level = this.floorSelector.floorData[levelIndex];

        let viewports = AecData.findViewportsForLevel(this.aec, level.guid);

        viewports = viewports.filter(AecData.supports2DTo3DTransform);

        if (viewports.length === 0) {
            Autodesk.Viewing.Private.logger.warn('No supported viewports for current level');
            return [];
        }

        const sheets = [];
        const box = new THREE.Box3();
        const tmpSize = new THREE.Vector3();

        viewports.forEach(viewport => {
            const geometries = this.rootNode.search({ type:'geometry', role: '2d', guid: viewport.sheetGuid} );
            if (geometries.length) {
                box.set(viewport.sectionBox.min, viewport.sectionBox.max);
                const transform = Autodesk.Viewing.BubbleNode.readMatrixFromArray12(viewport.sectionBox.transform);
                box.applyMatrix4(transform);
                const size = box.getSize(tmpSize);
                sheets.push({ viewport, node: geometries[0], boxSize: size.x * size.y });
            }
        });

        // Sort viewports by size, give priority to one with largest extent
        // This might not work well in the future because it depends on the sectionBox being correct, and this is only
        // true when `isCropBoxActive` is set during export from Revit. Right now this is the only supported situation
        // (see supports2DTo3DTransform), but it might change in the future.
        // TODO make sorting an option (with maybe other types of sort such as title)
        sheets.sort((a,b) => b.boxSize - a.boxSize);

        this.sheetsPerLevelCache[levelIndex] = sheets;

        return sheets;
    }

    /**
     * Loads a sheet corresponding to a level
     * @param {number} levelIndex
     * @param {number} [sheetIndex=0] - the index of the sheet according to the order returned by {@link getAvailableSheetsForLevel}
     * @param {function} [cb=null] - an optional callback to run after the sheet has been loaded
     */
    loadSheetFromLevel(levelIndex, sheetIndex = 0, cb = null) {
        const sheets = this.getAvailableSheetsForLevel(levelIndex);

        if (!sheets.length) {
            return;
        }

        const selectedSheet = sheets[sheetIndex];
        const hash = this.getHashNameForViewportAndLevel(selectedSheet.viewport, levelIndex);
        const node = selectedSheet.node;

        if (this.loadedSheets[hash]) {
            const loadedSheet = this.loadedSheets[hash];
            if (!loadedSheet.loading) {
                // If already loaded just make sure sheet is visible
                this.viewer.showModel(loadedSheet.model);
            }
            cb && cb({ hash, model: loadedSheet.model });
            return;
        }

        const options = {
            isAEC: true,
            useConsolidation: false,
            preserveView: true,
            keepCurrentModels: true,
            disable3DModelLayers: true,
            disablePrecomputedNodeBoxes: true,
            noShadow: true,
            doNotCut: true,
            customHash: hash,
        };

        if (this.options.hidePaper) {
            options.modelSpace = true;
            options.bgColor = 0x0;
        }

        this.loadedSheets[hash] = { node, levelIndex, sheetIndex, viewport: selectedSheet.viewport, loading: true };

        this.viewer.loadDocumentNode(this.document, node, options).then((model) => {
            // Check if sheet hasn't been unloaded in the meantime
            if (!this.loadedSheets[hash]) {
                cb && cb({});
                return;
            }

            this.loadedSheets[hash].loading = false;
            this.loadedSheets[hash].model = model;
            // When loading the 2D sheet on a 3D model, the visible bounds z length is larger than it should be
            // Force a recalculation so that we get a correct value (needed for the SheetRenderContext)
            model.invalidateBBoxes();
            cb && cb({hash, model});
        });
    }

    findLoadedSheetsFromLevelIndex(levelIndex) {
        const ret = []; // There might be more than one sheet loaded per level
        for (const sheet in this.loadedSheets) {
            if (this.loadedSheets[sheet].levelIndex === levelIndex) {
                ret.push(sheet);
            }
        }

        return ret;
    }

    findLoadedSheetFromLevelAndSheetIndex(levelIndex, sheetIndex) {
        for (const sheet in this.loadedSheets) {
            if (this.loadedSheets[sheet].levelIndex === levelIndex && this.loadedSheets[sheet].sheetIndex === sheetIndex) {
                return sheet;
            }
        }
    }

    unloadSheet(hash) {
        const sheet = this.loadedSheets[hash];
        if (!sheet) {
            return;
        }

        this.viewer.unloadDocumentNode(sheet.node);
        delete this.loadedSheets[hash];
    }

    hideModel(hash) {
        const sheet = this.loadedSheets[hash];

        if (sheet) {
            if (sheet.model) {
                this.viewer.hideModel(sheet.model);
            } else if (sheet.loading) {
                this.unloadSheet(hash);
            }
        }
    }

    hideSheetFromLevel(levelIndex, sheetIndex) {
        const sheet = this.findLoadedSheetFromLevelAndSheetIndex(levelIndex, sheetIndex);
        if (sheet) {
            this.hideModel(sheet);
        }
    }

    hideAllSheetsFromLevel(levelIndex) {
        const sheets = this.findLoadedSheetsFromLevelIndex(levelIndex);
        sheets.forEach(sheet => {
            this.hideModel(sheet);
        });
    }

    unloadSheetsFromLevel(levelIndex) {
        const sheets = this.findLoadedSheetsFromLevelIndex(levelIndex);
        sheets.forEach(sheet => {
            this.unloadSheet(sheet);
        });
    }

    unloadAll() {
        for (const sheet in this.loadedSheets) {
            this.viewer.unloadDocumentNode(this.loadedSheets[sheet].node);
        }

        this.loadedSheets = {};
    }
}

namespace.HypermodelingExtension = HypermodelingExtension;
Autodesk.Viewing.theExtensionManager.registerExtension(extensionName, HypermodelingExtension);
